import {httpsCallable} from "firebase/functions";
import {functions} from "../firebase/config";

// Callable functions for character sheet operations
const uploadCharacterSheetFunction = httpsCallable(
  functions,
  "uploadCharacterSheet"
);
const replaceCharacterSheetFunction = httpsCallable(
  functions,
  "replaceCharacterSheet"
);
const deleteCharacterSheetFunction = httpsCallable(
  functions,
  "deleteCharacterSheet"
);
const getSessionCharacterSheetsFunction = httpsCallable(
  functions,
  "getSessionCharacterSheets"
);
const getSessionPlayerLogsFunction = httpsCallable(
  functions,
  "getSessionPlayerLogs"
);
const refreshCharacterSheetUrlFunction = httpsCallable(
  functions,
  "refreshCharacterSheetUrl"
);

export interface CharacterSheetUploadData {
  characterId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  fileData: string; // Base64 encoded
}

export interface CharacterSheetReplaceData extends CharacterSheetUploadData {
  oldSheetUrl?: string;
}

export interface CharacterSheetDeleteData {
  characterId: string;
  // Optional legacy param; server prefers stored sheetPath
  sheetUrl?: string;
}

export interface SessionCharacterSheet {
  playerId: string;
  playerName: string;
  characterId: string;
  characterName: string;
  characterClass: string;
  characterLevel: number;
  sheetUrl?: string;
  hasSheet: boolean;
}

export interface SessionPlayerLog {
  playerId: string;
  playerName: string;
  characterId: string;
  characterName: string;
  characterClass: string;
  characterLevel: number;
  logData: {
    sessionId: string;
    characterId: string;
    playerId: string;
    sessionName: string;
    sessionDate: any;
    events: string;
    currency: {
      platinum: number;
      gold: number;
      electrum: number;
      silver: number;
      copper: number;
    };
    items: Array<{
      name: string;
      quantity: number;
      description: string;
      attunement: boolean;
    }>;
    mementoTokensAwarded: number;
    downtimeDaysAwarded: number;
    loggedAt: any;
  } | null;
  hasLogged: boolean;
}

/**
 * Upload a new character sheet
 */
export const uploadCharacterSheet = async (
  data: CharacterSheetUploadData
): Promise<{success: boolean; sheetPath: string; sheetUrl: string; message: string}> => {
  try {
    const result = await uploadCharacterSheetFunction(data);
    return result.data as {
      success: boolean;
      sheetPath: string;
      sheetUrl: string; // ephemeral
      message: string;
    };
  } catch (error: any) {
    throw new Error(error.message || "Failed to upload character sheet");
  }
};

/**
 * Replace an existing character sheet
 */
export const replaceCharacterSheet = async (
  data: CharacterSheetReplaceData
): Promise<{success: boolean; sheetPath: string; sheetUrl: string; message: string}> => {
  try {
    const result = await replaceCharacterSheetFunction(data);
    return result.data as {
      success: boolean;
      sheetPath: string;
      sheetUrl: string; // ephemeral
      message: string;
    };
  } catch (error: any) {
    throw new Error(error.message || "Failed to replace character sheet");
  }
};

/**
 * Delete a character sheet
 */
export const deleteCharacterSheet = async (
  data: CharacterSheetDeleteData
): Promise<{success: boolean; message: string}> => {
  try {
    const result = await deleteCharacterSheetFunction(data);
    return result.data as {success: boolean; message: string};
  } catch (error: any) {
    throw new Error(error.message || "Failed to delete character sheet");
  }
};

/**
 * Get a fresh, short-lived signed URL for viewing/downloading a character sheet
 */
export const refreshCharacterSheetUrl = async (
  characterId: string
): Promise<{success: boolean; sheetUrl: string; message: string}> => {
  try {
    const result = await refreshCharacterSheetUrlFunction({characterId});
    return result.data as {success: boolean; sheetUrl: string; message: string};
  } catch (error: any) {
    throw new Error(error.message || "Failed to refresh character sheet URL");
  }
};

/**
 * Get character sheets for all players in a session
 * This allows DMs to view and download character sheets for their sessions
 */
export const getSessionCharacterSheets = async (
  sessionId: string
): Promise<{characterSheets: SessionCharacterSheet[]}> => {
  try {
    const result = await getSessionCharacterSheetsFunction({sessionId});
    return result.data as {characterSheets: SessionCharacterSheet[]};
  } catch (error: any) {
    throw new Error(error.message || "Failed to get session character sheets");
  }
};

/**
 * Get player logs for all players in a session
 * This allows DMs to view who has logged their session participation
 */
export const getSessionPlayerLogs = async (
  sessionId: string
): Promise<{playerLogs: SessionPlayerLog[]}> => {
  try {
    const result = await getSessionPlayerLogsFunction({sessionId});
    return result.data as {playerLogs: SessionPlayerLog[]};
  } catch (error: any) {
    throw new Error(error.message || "Failed to get session player logs");
  }
};
